import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Header,
  Icon,
  Modal,
  Popup,
  Progress,
} from "semantic-ui-react";
import JSZip from "jszip";
import CONSTANTS from "constants/Constants";

import styles from "routes/data_tab/components/forms/ImportForm.module.scss";
import { handleDownload } from "routes/data_tab/helpers";
import DataJobService from "services/DataJob";
import ProductLineService from "services/ProductLine";
import TagService from "services/Tag";
import FilePicker from "components/FilePicker";
import RuvixxForm from "components/RuvixxForm";
import UploadManager from "components/UploadManager";

const DownloadImportTemplate = () => {
  const templateName = "Case Data";

  const getImportTemplate = async () => {
    let guidelines = [];
    let fields = [];
    const rawTemplate = await DataJobService.getCaseDataImportTemplate();

    for (const row in rawTemplate) {
      fields.push(row);
      guidelines.push([row, rawTemplate[row]]);
    }

    fields.shift();

    const templateConfig = [
      {
        data: [fields],
        sheetName: `${templateName} Import Template`,
      },
      {
        data: guidelines,
        sheetName: `${templateName} Import Guidelines`,
      },
    ];

    await handleDownload(
      templateConfig,
      `${templateName} Import Template.xlsx`
    );
  };

  return (
    <div className="validation-template">
      <Button
        className="template-download"
        onClick={getImportTemplate}
        size="small"
      >
        <Icon name="download" />
        Download Template
      </Button>
    </div>
  );
};

const TIMEOUT = 5000;

function mapTagsForSelect(item) {
  return {
    key: item.id,
    text: item.name,
    value: item.id,
    ...(item.color && {
      className: "hideText",
      label: {
        content: item.name,
        className: "ctag",
        style: { backgroundColor: item.color },
      },
    }),
  };
}

const CaseDataImportForm = props => {
  const [cancelRequest, setCancelRequest] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [success, setSuccess] = useState(false);
  const [stage, setStage] = useState(props.dataJobId ? "importing" : "init");
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showProgress, setShowProgress] = useState(!!props.dataJobId); // Show progress if reopening existing job
  const [isFileValid, setIsFileValid] = useState(false);
  const [dataJobId, setDataJobId] = useState(props.dataJobId);
  const [dataJobStatus, setDataJobStatus] = useState();
  const [progressListenerId, setProgressListenerId] = useState();
  const [successListenerId, setSuccessListenerId] = useState();
  const [numberOfFiles, setNumberOfFiles] = useState(0);
  const [processedFiles, setProcessedFiles] = useState(0);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [productLineOptions, setProductLineOptions] = useState([]);
  const [selectedProductLineId, setSelectedProductLineId] = useState(null);
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [processingStage, setProcessingStage] = useState("idle"); // idle, validating, extracting, uploading, processing
  const [stageProgress, setStageProgress] = useState({
    extracted: 0,
    totalToExtract: 0,
    uploaded: 0,
    totalToUpload: 0,
    processed: 0,
    totalToProcess: 0,
  });

  const uploadManager = useMemo(() => new UploadManager(), []);

  // Memory management constants
  const MAX_MEMORY_MB = 1024; // 1GB max memory usage
  const MAX_FILE_SIZE_MB = 500; // 500MB max per individual file
  const MAX_TOTAL_SIZE_MB = 5120; // 5GB max total uncompressed size

  // Check if browser supports required APIs
  const browserSupportsClientProcessing = () => {
    return (
      window.File &&
      window.FileReader &&
      window.Blob &&
      typeof JSZip !== "undefined"
    );
  };

  // Monitor memory usage
  const checkMemoryUsage = () => {
    if (performance.memory) {
      const { usedJSHeapSize, jsHeapSizeLimit } = performance.memory;
      const usagePercent = (usedJSHeapSize / jsHeapSizeLimit) * 100;
      return usagePercent < 80; // Don't exceed 80% of heap limit
    }
    return true; // Assume safe if can't check
  };

  // Recursively extract CSV files from nested zip files
  const extractCsvFiles = async (zipOrFile, basePath = "") => {
    const extractedFiles = [];

    if (zipOrFile instanceof File) {
      // If it's a File object, try to load it as a zip
      try {
        const zip = await JSZip.loadAsync(zipOrFile);
        const nestedFiles = await extractCsvFiles(zip, basePath);
        extractedFiles.push(...nestedFiles);
      } catch (error) {
        // If it's not a valid zip, ignore it
        console.warn(`File ${zipOrFile.name} is not a valid zip file:`, error);
      }
    } else {
      // It's a JSZip object
      for (const [filename, file] of Object.entries(zipOrFile.files)) {
        // Skip directories and macOS metadata files
        if (
          file.dir ||
          filename.includes("__MACOSX") ||
          filename.includes(".DS_Store")
        ) {
          continue;
        }

        const fullPath = basePath ? `${basePath}/${filename}` : filename;
        const fileSize = file._data.uncompressedSize;
        extractedFiles.push({
          filename: fullPath,
          file,
          size: fileSize,
        });
      }
    }

    return extractedFiles;
  };

  // Validate zip file before processing
  const validateZipFile = async zipFile => {
    try {
      setProcessingStage("validating");

      // Recursively extract all CSV files from the zip (and any nested zips)
      const allCsvFiles = await extractCsvFiles(zipFile);

      let totalSize = 0;
      const oversizedFiles = [];
      const validFiles = [];

      for (const csvFile of allCsvFiles) {
        totalSize += csvFile.size;

        if (csvFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          oversizedFiles.push({
            filename: csvFile.filename,
            size: csvFile.size,
          });
        } else {
          validFiles.push(csvFile);
        }
      }

      const isValid =
        totalSize <= MAX_TOTAL_SIZE_MB * 1024 * 1024 &&
        oversizedFiles.length === 0 &&
        allCsvFiles.length > 0;

      return {
        isValid,
        totalSize,
        fileCount: allCsvFiles.length,
        oversizedFiles,
        validFiles: validFiles.sort((a, b) => a.size - b.size), // Process smaller files first
      };
    } catch (error) {
      console.error("Failed to validate zip file:", error);
      return { isValid: false, error: error.message };
    }
  };

  // Create memory-safe batches
  const createMemoryBatches = (files, maxBytes) => {
    const batches = [];
    let currentBatch = [];
    let currentSize = 0;
    const maxFilesPerBatch = 10; // Limit to 10 files per batch for better processing

    for (const file of files) {
      if (
        (currentSize + file.size > maxBytes ||
          currentBatch.length >= maxFilesPerBatch) &&
        currentBatch.length > 0
      ) {
        batches.push(currentBatch);
        currentBatch = [file];
        currentSize = file.size;
      } else {
        currentBatch.push(file);
        currentSize += file.size;
      }
    }

    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches;
  };

  // Process a batch of files
  const processBatch = async (batch, batchIndex, jobId) => {
    const extractedFiles = [];

    for (let i = 0; i < batch.length; i++) {
      const { filename, file } = batch[i];

      // Check memory before extraction
      if (!checkMemoryUsage()) {
        console.warn("High memory usage detected, pausing extraction");
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (!checkMemoryUsage()) {
          throw new Error("Memory usage too high, cannot continue extraction");
        }
      }

      try {
        const fileBlob = await file.async("blob");
        // Use the full path as filename to preserve nested structure
        const cleanFilename = filename.split("/").pop(); // Get just the filename without path
        const fileObject = new File([fileBlob], cleanFilename, {
          type: "text/csv",
        });
        extractedFiles.push(fileObject);

        // Update extraction progress
        setStageProgress(prev => ({
          ...prev,
          extracted: prev.extracted + 1,
        }));
      } catch (error) {
        console.error(`Failed to extract ${filename}:`, error);
        throw new Error(`Failed to extract ${filename}: ${error.message}`);
      }
    }

    // Upload this batch
    await uploadBatch(extractedFiles, batchIndex, jobId);

    // Clear references to help GC
    extractedFiles.length = 0;

    // Force garbage collection if available
    if (window.gc) {
      window.gc();
    }
  };

  // Upload a batch of extracted files and send to backend for processing
  const uploadBatch = async (extractedFiles, batchIndex, jobId) => {
    try {
      setProcessingStage("uploading");

      await uploadManager.uploadFiles(extractedFiles);

      // Wait for upload completion by polling the UploadManager directly
      const uploadedFiles = await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 300; // 30 seconds timeout

        const checkUploadComplete = () => {
          attempts++;

          // Get files directly from UploadManager's internal state
          const allUploads = uploadManager.uploads || [];
          const batchFilenames = extractedFiles.map(f => f.name);

          // Find uploads that match our batch files
          const batchUploads = allUploads.filter(upload =>
            batchFilenames.includes(upload.file?.name)
          );

          const completedUploads = batchUploads.filter(
            upload => upload.attachment && upload.status === "finished"
          );
          const failedUploads = batchUploads.filter(
            upload => upload.status === "failed"
          );

          if (attempts % 10 === 0) {
            // Log detailed status every 10 attempts
            console.log(
              "Batch uploads status:",
              batchUploads.map(u => ({
                name: u.file?.name,
                status: u.status,
                hasAttachment: !!u.attachment,
                attachmentData: u.attachment
                  ? {
                      file_name: u.attachment.file_name,
                      s3_key: u.attachment.s3_key,
                    }
                  : null,
              }))
            );
          }

          if (failedUploads.length > 0) {
            console.error(`Batch ${batchIndex} failed uploads:`, failedUploads);
            reject(new Error(`${failedUploads.length} files failed to upload`));
            return;
          }

          if (completedUploads.length >= extractedFiles.length) {
            resolve(completedUploads);
            return;
          }

          if (attempts >= maxAttempts) {
            console.error(
              `Batch ${batchIndex} upload timeout after ${attempts} attempts`
            );
            reject(new Error(`Upload timeout after ${maxAttempts} attempts`));
            return;
          }

          // Check again in 100ms
          setTimeout(checkUploadComplete, 100);
        };

        checkUploadComplete();
      });

      // Collect S3 metadata for backend processing
      const fileMetadata = uploadedFiles.map(file => {
        const metadata = {
          file_name: file.attachment.file_name,
          s3_key: file.attachment.s3_key,
          content_type:
            file.attachment.file_content_type ||
            file.attachment.file_type ||
            file.attachment.content_type ||
            "text/csv",
          file_size: file.attachment.file_size,
        };

        return metadata;
      });

      // Send batch to backend for case data processing
      if (jobId) {
        try {
          const response = await DataJobService.processCaseDataFiles(
            jobId,
            fileMetadata
          );

          if (!response.success) {
            console.warn(
              `Batch ${batchIndex} processing warnings:`,
              response.errors
            );
          } else {
            console.log(
              `Batch ${batchIndex} successfully processed ${response.processed_count} files`
            );
          }
        } catch (error) {
          console.error(`Batch ${batchIndex} backend processing error:`, error);
          throw error;
        }
      } else {
        console.warn(`No jobId available for batch ${batchIndex}`);
      }

      // Update upload progress
      setStageProgress(prev => ({
        ...prev,
        uploaded: prev.uploaded + uploadedFiles.length,
        processed: prev.processed + fileMetadata.length,
      }));
    } catch (error) {
      console.error(`Failed to upload batch ${batchIndex}:`, error);
      throw new Error(`Failed to upload files: ${error.message}`);
    }
  };

  // Main zip processing function
  const processZipFile = async (zipFile, jobId) => {
    try {
      if (!browserSupportsClientProcessing()) {
        throw new Error("Browser doesn't support client-side zip processing");
      }

      // Validate the zip file
      const validation = await validateZipFile(zipFile);

      if (!validation.isValid) {
        let errorMsg = "Invalid zip file";
        if (validation.oversizedFiles?.length > 0) {
          errorMsg = `File(s) too large: ${validation.oversizedFiles
            .map(f => f.filename)
            .join(", ")}`;
        } else if (validation.totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
          errorMsg = `Total uncompressed size too large: ${(
            validation.totalSize /
            (1024 * 1024)
          ).toFixed(1)}MB`;
        } else if (validation.fileCount === 0) {
          errorMsg =
            "No valid CSV files found in zip or nested zip files. Please ensure your zip contains .csv files (may be nested in other zip files).";
        }
        throw new Error(errorMsg);
      }

      // Initialize progress tracking
      setStageProgress({
        extracted: 0,
        totalToExtract: validation.fileCount,
        uploaded: 0,
        totalToUpload: validation.fileCount,
        processed: 0,
        totalToProcess: validation.fileCount,
      });

      setProcessingStage("extracting");

      // Create memory-safe batches
      const maxBatchSize = (MAX_MEMORY_MB * 1024 * 1024) / 2; // Use half of max memory per batch
      const batches = createMemoryBatches(validation.validFiles, maxBatchSize);

      // Process batches sequentially to manage memory
      for (let i = 0; i < batches.length; i++) {
        await processBatch(batches[i], i + 1, jobId);

        // Small delay between batches
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Mark upload as complete and start backend processing
      try {
        if (jobId) {
          await DataJobService.markCaseDataUploadComplete(jobId);
          setProcessingStage("processing");
        }
      } catch (error) {
        console.error("Failed to mark upload complete:", error);
        // Continue anyway as files are already uploaded and processing tasks queued
      }

      setProcessingStage("uploaded");
      return true;
    } catch (error) {
      console.error("Zip processing failed:", error);
      setError(true);
      setErrorMessage(error.message);
      setProcessingStage("idle");
      throw error;
    }
  };

  function handleProgress(files) {
    setFiles([...files]); // careful, not enough to be considered immutable...
  }

  const handleSuccess = useCallback(async () => {
    if (files.every(file => file.status === "finished")) {
      // If we're in uploading stage and don't have a dataJobId, create data job for CSV files
      if (stage === "uploading" && !dataJobId) {
        try {
          const newDataJob = await DataJobService.createCaseDataImportJob(
            files.map(file => file.attachment),
            { tag_ids: tagIds, product_line_id: selectedProductLineId },
            description
          );
          setDataJobId(newDataJob.id);
          setSubmitted(true);
        } catch (error) {
          setError(true);
          setErrorMessage("Failed to create import job: " + error.message);
        }
      } else {
        setStage("uploaded");
      }
    }
  }, [files, stage, dataJobId, tagIds, selectedProductLineId, description]);

  const fetchTags = async () => {
    let tags = await TagService.getTags({ model: "case" });
    setTagOptions(tags.map(mapTagsForSelect));
  };

  const fetchProductLines = async () => {
    const productLines = await ProductLineService.getForFilters();
    setProductLineOptions(
      productLines.map(({ id, name }) => ({
        key: id,
        value: id,
        text: name,
      }))
    );
  };

  useEffect(() => {
    setProgressListenerId(uploadManager.addProgressListener(handleProgress));
    setSuccessListenerId(uploadManager.addSuccessListener(handleSuccess));
    fetchTags();
    fetchProductLines();

    return function cleanup() {
      uploadManager.removeProgressListener(progressListenerId || 0);
      uploadManager.removeSuccessListener(successListenerId || 0);
    };
  }, [uploadManager]);

  useEffect(() => {
    let timeout, started;

    async function monitor(jobId) {
      try {
        const dataJob = await DataJobService.getDataJob(jobId);
        const { info, status } = dataJob;
        if (dataJob.description) {
          setDescription(dataJob.description);
        }

        setDataJobStatus(status);
        if (status === CONSTANTS.DATA_JOB_STATUSES.ABORTED) {
          setStage("aborted");
          setShowProgress(false);
          return;
        }
        setNumberOfFiles(info.number_of_files || 0);
        setProcessedFiles(info.processed_files || 0);

        // Set processing stage when reopening in-progress job
        if (
          status === CONSTANTS.DATA_JOB_STATUSES.IN_PROGRESS &&
          processingStage === "idle"
        ) {
          setProcessingStage("processing");
        }
        let doTimeout = false;
        switch (status) {
          case CONSTANTS.DATA_JOB_STATUSES.FAILED:
            setStage("failed");
            setError(true);
            setErrorMessage(info.error?.message);
            setSubmitted(true);
            setShowProgress(false);
            break;
          case CONSTANTS.DATA_JOB_STATUSES.COMPLETE:
            setStage("imported");
            setShowProgress(false);
            break;
          case CONSTANTS.DATA_JOB_STATUSES.EXPIRED:
            setShowProgress(false);
            break;
          default:
            // still importing
            doTimeout = true;
        }

        if (doTimeout) {
          timeout = setTimeout(() => {
            monitor(jobId);
          }, TIMEOUT);
        }
      } catch (e) {
        // uncaught errors
        if (timeout) {
          clearTimeout(timeout);
        }
        setStage("errored");
        setSuccess(false);
        setError(true);
        setErrorMessage(
          error && "message" in error
            ? error.message
            : error?.toString() || "Unknown error"
        );
      }
    }

    if (dataJobId && !started) {
      started = true;
      monitor(dataJobId);
    }

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [dataJobId]);

  async function startImport() {
    try {
      setStage("importing");
      setSubmitted(true);
      setShowProgress(true); // Show progress UI

      // Only .zip uploads are accepted (enforced by selectFiles and the
      // FilePicker), so the selection is always a single zip processed
      // client-side.
      const zipFile = selectedFiles[0];

      // Create empty data job first for client-side processing
      const newDataJob = await DataJobService.createCaseDataImportJob(
        [], // Empty attachments for client-side processing
        { tag_ids: tagIds, product_line_id: selectedProductLineId },
        description
      );
      setDataJobId(newDataJob.id);

      setStage("extracting");
      // Pass the dataJobId directly to processZipFile instead of relying on state
      await processZipFile(zipFile, newDataJob.id);
    } catch (error) {
      setStage("failed");
      setError(true);
      setErrorMessage("message" in error ? error.message : error.toString());
      setProcessingStage("idle");
      setShowProgress(false);
    }
  }

  function handleCancelRequest() {
    if (dataJobId) {
      setCancelRequest(true);
    } else {
      props.refreshTable();
      props.onClose();
    }
  }

  function handleContinue() {
    setCancelRequest(false);
  }

  function handleAbort() {
    if (dataJobId) {
      DataJobService.abortImport(dataJobId); //dont await
    }

    props.refreshTable();
    props.onClose();
  }

  function handleHide() {
    props.refreshTable();
    props.onClose();
  }

  function handleDescriptionChange(_, { value: description }) {
    if (description.length <= 255) {
      setDescription(description);
    }
  }

  function removeSelectedFile(indexToRemove) {
    const updatedFiles = selectedFiles.filter(
      (_, index) => index !== indexToRemove
    );
    setSelectedFiles(updatedFiles);

    // Reset validation if no files left
    if (updatedFiles.length === 0) {
      setIsFileValid(false);
      setStage("init");
    }
  }

  function selectFiles(files, event, dropped) {
    if (!files) {
      setSuccess(false);
      setError(false);
      setErrorMessage(null);
      return;
    }
    if (files.length === 0) {
      setSuccess(false);
      setError(true);
      setErrorMessage("No file was selected.");
      return;
    }

    // Reset progress state
    setProcessingStage("idle");
    setStageProgress({
      extracted: 0,
      totalToExtract: 0,
      uploaded: 0,
      totalToUpload: 0,
      processed: 0,
      totalToProcess: 0,
    });

    // Validate file types
    let errorMessage = null;
    let isFileValid = files.every(file => file.name.endsWith(".zip"));
    errorMessage = isFileValid ? null : `Upload must be a .zip file.`;

    setIsFileValid(isFileValid);
    setSuccess(false);
    setError(!isFileValid);
    setErrorMessage(errorMessage);

    if (isFileValid) {
      if (files.length > 1) {
        setError(true);
        setErrorMessage("Please upload only one zip file at a time.");
        return;
      }

      // Check browser support for zip files
      if (!browserSupportsClientProcessing()) {
        setError(true);
        setErrorMessage(
          "Your browser doesn't support client-side zip processing. Please try a different browser."
        );
        return;
      }

      // Store selected files and show them in UI
      setSelectedFiles(files);
      setStage("uploaded"); // This shows the Import button
    }
  }

  const statusMessage = Object.keys(CONSTANTS.DATA_JOB_STATUSES).map(key => {
    const status = key.toLowerCase();
    return status.replaceAll("_", " ");
  });
  const isExpired = dataJobStatus === CONSTANTS.DATA_JOB_STATUSES.EXPIRED;
  return (
    <RuvixxForm
      ready
      error={error}
      errorMessage={errorMessage}
      success={success}
      extraButtons={[
        !isExpired &&
          [
            CONSTANTS.DATA_JOB_STATUSES.IN_PROGRESS,
            CONSTANTS.DATA_JOB_STATUSES.SCHEDULED,
            CONSTANTS.DATA_JOB_STATUSES.READY,
          ].includes(dataJobStatus) && (
            <Button
              secondary
              basic
              onClick={handleCancelRequest}
              compact
              size="tiny"
            >
              Cancel
            </Button>
          ),

        <Button
          secondary
          onClick={handleHide}
          compact
          size="tiny"
          content="Close"
        />,
      ]}
      submitted={submitted}
    >
      <div className={styles["case-validation-grid"]}>
        {/* Only show form inputs when not in progress */}
        {!showProgress && (stage === "init" || stage === "uploaded") && (
          <>
            <FilePicker
              multiple
              onSelect={selectFiles}
              accept={".zip"}
              primary
            />
            <Form.Select
              selectOnBlur={false}
              key="tags"
              multiple
              label="Tag"
              inline
              search
              options={tagOptions}
              onChange={(e, { value }) => setTagIds(value)}
              placeholder="Choose a Tag"
              renderLabel={item => item.label || item.text}
              className="importer-selector"
            />
            <Form.Select
              selectOnBlur={false}
              key="productLine"
              required
              clearable
              label="Product Line"
              inline
              search
              options={productLineOptions}
              value={selectedProductLineId}
              onChange={(e, { value }) => setSelectedProductLineId(value)}
              placeholder="Choose a Product Line"
              className="importer-selector"
            />
            <div className={styles["description-container"]}>
              <Form.TextArea
                required
                inline
                label="Description"
                style={{ height: "43px" }}
                onChange={handleDescriptionChange}
                value={description}
                placeholder="Description..."
              />
              <span className={styles["description-counter"]}>
                {description.length}/255
              </span>
            </div>
            {["init", "uploaded"].includes(stage) && (
              <p
                className={styles["validation-msg"]}
                style={{ marginBottom: "0.5em" }}
              >
                Before you upload your file, make sure it's in Pleteo format.
                File should be a zip file containing one or more .csv files.
              </p>
            )}
            <DownloadImportTemplate />
          </>
        )}

        {/* Enhanced selected files display */}
        {!showProgress && selectedFiles.length > 0 && (
          <div style={{ marginTop: "1.5em", marginBottom: "1.5em" }}>
            <strong
              style={{ fontSize: "1.1em", color: "var(--color-text-primary)" }}
            >
              Selected Files:
            </strong>
            <div
              style={{
                marginTop: "0.75em",
                border: "1px solid var(--color-border)",
                borderRadius: "4px",
                backgroundColor: "var(--color-surface)",
              }}
            >
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  style={{
                    padding: "0.75em 1em",
                    borderBottom:
                      index < selectedFiles.length - 1
                        ? "1px solid var(--color-border)"
                        : "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "var(--color-surface-raised)",
                  }}
                >
                  <div
                    style={{ flex: 1, display: "flex", alignItems: "center" }}
                  >
                    <Icon
                      name="file text"
                      color="blue"
                      style={{ marginRight: "0.5em" }}
                    />
                    <span style={{ fontWeight: "500" }}>{file.name}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1em",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--color-text-muted)",
                        fontSize: "0.9em",
                      }}
                    >
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <Button
                      size="mini"
                      basic
                      circular
                      icon="close"
                      color="red"
                      onClick={() => removeSelectedFile(index)}
                      title="Remove file"
                      style={{ padding: "0.3em" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!showProgress && stage === "uploaded" && !dataJobId && (
          <Popup
            disabled={!isExpired}
            content="Data Job has expired"
            position="top center"
            on="hover"
            trigger={
              <div style={{ display: "inline-block" }}>
                <Button
                  style={{ marginTop: "1em" }}
                  disabled={
                    !selectedFiles.length ||
                    !isFileValid ||
                    !description ||
                    !selectedProductLineId ||
                    isExpired
                  }
                  className="item-adder"
                  content="Import"
                  onClick={startImport}
                />
              </div>
            }
          />
        )}
      </div>

      {/* Progress display - only show when in progress */}
      {showProgress && (
        <div style={{ marginTop: "2em" }}>
          {/* Client-side processing progress */}
          {processingStage === "validating" && (
            <div style={{ marginBottom: "1em" }}>
              <Icon name="circle notched" loading />
              Validating zip file...
            </div>
          )}

          {processingStage === "extracting" && (
            <div style={{ marginBottom: "1em" }}>
              <Icon name="circle notched" loading />
              Extracting files... ({stageProgress.extracted} of{" "}
              {stageProgress.totalToExtract} extracted)
              <Progress
                percent={
                  stageProgress.totalToExtract > 0
                    ? (stageProgress.extracted / stageProgress.totalToExtract) *
                      100
                    : 0
                }
                size="tiny"
                color="blue"
              />
            </div>
          )}

          {processingStage === "uploading" && (
            <div style={{ marginBottom: "1em" }}>
              <Icon name="cloud upload" color="green" />
              Uploading files to S3... ({stageProgress.uploaded} of{" "}
              {stageProgress.totalToUpload} uploaded)
              {console.log("Upload progress debug:", {
                uploaded: stageProgress.uploaded,
                totalToUpload: stageProgress.totalToUpload,
                numberOfFiles,
                processedFiles,
              })}
              <Progress
                percent={
                  stageProgress.totalToUpload > 0
                    ? (stageProgress.uploaded / stageProgress.totalToUpload) *
                      100
                    : 0
                }
                size="tiny"
                color="green"
              />
            </div>
          )}

          {/* Server-side processing progress - show alongside uploading */}
          {(stage === "importing" ||
            processingStage === "processing" ||
            dataJobStatus === CONSTANTS.DATA_JOB_STATUSES.IN_PROGRESS ||
            (numberOfFiles > 0 && dataJobId) ||
            (processedFiles > 0 && dataJobId)) && (
            <div style={{ marginBottom: "1em" }}>
              <Icon name="cogs" color="orange" />
              {numberOfFiles > 0
                ? `Processing case data files... (${processedFiles || 0} of ${
                    stageProgress.totalToUpload || numberOfFiles
                  } complete)`
                : "Processing case data files..."}
              {numberOfFiles > 0 && (
                <Progress
                  percent={((processedFiles || 0) / numberOfFiles) * 100}
                  size="tiny"
                  color="orange"
                />
              )}
            </div>
          )}
        </div>
      )}

      {dataJobStatus >= CONSTANTS.DATA_JOB_STATUSES.IN_PROGRESS && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "2em",
          }}
        >
          {/* Show completion summary when complete, otherwise show status */}
          {dataJobStatus === CONSTANTS.DATA_JOB_STATUSES.COMPLETE &&
          numberOfFiles > 0 ? (
            <div
              style={{
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "8px",
                padding: "1.5em",
                textAlign: "center",
                minWidth: "300px",
              }}
            >
              <Icon
                name="check circle"
                color="green"
                size="large"
                style={{ marginBottom: "0.5em" }}
              />
              <div
                style={{
                  fontSize: "1.1em",
                  fontWeight: "bold",
                  color: "#28a745",
                  marginBottom: "0.75em",
                }}
              >
                Import Completed Successfully
              </div>
              <div style={{ color: "var(--color-text-secondary)" }}>
                <div>
                  <strong>{processedFiles}</strong> files imported
                </div>
              </div>
            </div>
          ) : isExpired ? (
            <div
              style={{
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "8px",
                padding: "1.5em",
                textAlign: "center",
                minWidth: "300px",
              }}
            >
              <Icon
                name="clock outline"
                color="grey"
                size="large"
                style={{ marginBottom: "0.5em" }}
              />
              <div
                style={{
                  fontSize: "1.1em",
                  fontWeight: "bold",
                  color: "var(--color-text-muted)",
                  marginBottom: "0.75em",
                }}
              >
                Data Job Expired
              </div>
              <div style={{ color: "var(--color-text-secondary)" }}>
                This job was inactive for over 24 hours and has been
                automatically expired. Please create a new import to try again.
              </div>
            </div>
          ) : (
            <>
              <span
                style={{
                  fontSize: "1.2em",
                  fontWeight: "bold",
                  marginBottom: "0.5em",
                }}
              >
                Import {statusMessage[dataJobStatus - 1]}
              </span>
              <span
                style={{
                  color: "var(--color-text-muted)",
                  marginBottom: "1em",
                }}
              >
                {description}
              </span>
            </>
          )}
        </div>
      )}

      {cancelRequest ? (
        <Modal open={true} onClose={handleContinue} size="small">
          <Header icon="warning sign" content="Abort Process?" />
          <Modal.Content>
            This operation will abort the process and you will have to start
            again.
          </Modal.Content>
          <Modal.Actions>
            <Button
              className="warning cancel"
              inverted
              onClick={handleContinue}
            >
              <Icon name="remove" /> No
            </Button>
            <Button className="warning confirm" inverted onClick={handleAbort}>
              <Icon name="checkmark" /> Yes
            </Button>
          </Modal.Actions>
        </Modal>
      ) : null}
    </RuvixxForm>
  );
};
export default CaseDataImportForm;
